import Api from "./PreApiHandler";

export default {
    getStatus,
    getHistory,
    getEstimate,
    getPricing,
    getAiModelPrices,
    getAiAssistantTargets,
    getMailingQuota,
    topup,
    changePlan,
    changeCurrency,
    getPaymentMethods,
    deletePaymentMethod,
    setAutopayMethod,
    setAutoRecharge,
    createStaticWallet,
    createRecurring,
};

/** Current plan, balance, usage, and monthly estimate */
async function getStatus(asUserId = null) {
    const params = asUserId ? { as_user_id: asUserId } : undefined;
    return Api.sendRequest({ method: 'get', path: 'user/billing-v2/status', params });
}

/**
 * Paginated billing history.
 * @param {object} params - { page, per_page, type? }
 * @param {number|null} asUserId - admin impersonation
 */
async function getHistory(params = {}, asUserId = null) {
    const p = asUserId ? { ...params, as_user_id: asUserId } : params;
    return Api.sendRequest({ method: 'get', path: 'user/billing-v2/history', params: p });
}

/** Projected monthly cost based on current usage pace */
async function getEstimate(asUserId = null) {
    const params = asUserId ? { as_user_id: asUserId } : undefined;
    return Api.sendRequest({ method: 'get', path: 'user/billing-v2/estimate', params });
}

/** Public pricing: all plans with limits and tiers */
async function getPricing() {
    return Api.sendRequest({ method: 'get', path: 'user/billing-v2/pricing' });
}

/** Per-model AI pricing (final USD per 1k tokens, markup included) for the ai_agent node */
async function getAiModelPrices() {
    return Api.sendRequest({ method: 'get', path: 'user/billing-v2/ai-model-prices' });
}

/**
 * Projects the current user may charge AI-assistant usage to besides their own wallet — resources
 * they don't own but hold `ai_assistant.use` on. Each: { resource_key, name, owner_name, access,
 * mode, can_use }. Empty for owners with no cross-owner grants (the picker then offers only "my tokens").
 */
async function getAiAssistantTargets() {
    return Api.sendRequest({ method: 'get', path: 'user/billing-v2/ai-assistant-targets' });
}

/**
 * Create a top-up payment invoice.
 * @param {object} params - { sum: number, system: string, currency: string }
 */
async function topup(params) {
    return Api.sendRequest({ method: 'post', path: 'user/billing-v2/topup', params });
}

/**
 * Switch the user's plan.
 * @param {object} params - { plan_code: string }
 */
async function changePlan(params) {
    return Api.sendRequest({ method: 'post', path: 'user/billing-v2/change-plan', params });
}

/** List saved payment methods for the current user. */
async function getPaymentMethods() {
    return Api.sendRequest({ method: 'get', path: 'user/billing-v2/payment-methods' });
}

/**
 * Delete a saved payment method.
 * @param {number} id
 */
async function deletePaymentMethod(id) {
    return Api.sendRequest({ method: 'post', path: 'user/billing-v2/delete-payment-method', params: { id } });
}

/**
 * Set (or unset) a saved method as the autopay source.
 * @param {number}  id
 * @param {boolean} enabled
 */
async function setAutopayMethod(id, enabled) {
    return Api.sendRequest({ method: 'post', path: 'user/billing-v2/set-autopay-method', params: { id, enabled } });
}

/**
 * Configure threshold-based automatic balance top-up.
 * @param {object} params - { enabled: bool, threshold: number, amount: number, monthly_cap: number }
 */
async function setAutoRecharge(params) {
    return Api.sendRequest({ method: 'post', path: 'user/billing-v2/set-auto-recharge', params });
}

/**
 * Get or create a static crypto wallet for the user.
 * @param {object} params - { currency: string, network: string }
 */
async function createStaticWallet(params) {
    return Api.sendRequest({ method: 'post', path: 'user/billing-v2/cryptomus-static-wallet', params });
}

/**
 * Create a Cryptomus recurring plan.
 * @param {object} params - { amount: number, currency: string, period: string }
 */
async function createRecurring(params) {
    return Api.sendRequest({ method: 'post', path: 'user/billing-v2/cryptomus-recurring', params });
}

/**
 * Lightweight mailing quota check. Reads cached snapshot — no ClickHouse call.
 * Pass bot_key so the quota is checked against the bot owner's account,
 * not the caller's (relevant when the caller has shared access to someone else's bot).
 * Free plan: returns included/used/remaining counts and can_launch flag.
 * Paid plan: returns balance and can_launch flag.
 * @param {string|null} botKey
 */
async function getMailingQuota(botKey = null) {
    const params = botKey ? { bot_key: botKey } : undefined;
    return Api.sendRequest({ method: 'get', path: 'user/billing-v2/mailing-quota', params });
}

/**
 * Change billing currency. Allowed only when balance is zero.
 * @param {string} currency - 'USD' or 'RUB'
 */
async function changeCurrency(currency) {
    return Api.sendRequest({ method: 'post', path: 'user/billing-v2/change-currency', params: { currency } });
}
